/**
 * Component-specific responsive configurations
 * This file contains responsive values for different components to prevent overlapping
 */

import { ResponsiveValue } from "./breakpoints";

// SourceDoc component responsive widths
export const sourceDocConfig: ResponsiveValue<{
  width: string;
  right: string;
  maxWidth: string;
}> = {
  fourK: {
    width: "35%",
    right: "20px",
    maxWidth: "800px",
  },
  twoK: {
    width: "38%",
    right: "20px",
    maxWidth: "700px",
  },
  fhdToMacbook: {
    width: "40%",
    right: "20px",
    maxWidth: "650px", // Reduced for MacBook to prevent overlap
  },
  large: {
    width: "42%",
    right: "20px",
    maxWidth: "600px",
  },
  medium: {
    width: "45%",
    right: "20px",
    maxWidth: "550px",
  },
  small: {
    width: "90%",
    right: "5%",
    maxWidth: "100%",
  },
  default: {
    width: "40%",
    right: "20px",
    maxWidth: "650px",
  },
};

// CheckRunView component responsive widths
export const checkRunViewConfig: ResponsiveValue<{
  width: string;
  left: string;
  maxWidth: string;
}> = {
  fourK: {
    width: "35%",
    left: "0",
    maxWidth: "800px",
  },
  twoK: {
    width: "38%",
    left: "0",
    maxWidth: "700px",
  },
  fhdToMacbook: {
    width: "40%",
    left: "0",
    maxWidth: "650px", // Reduced for MacBook
  },
  large: {
    width: "42%",
    left: "0",
    maxWidth: "600px",
  },
  medium: {
    width: "45%",
    left: "0",
    maxWidth: "550px",
  },
  small: {
    width: "90%",
    left: "5%",
    maxWidth: "100%",
  },
  default: {
    width: "40%",
    left: "0",
    maxWidth: "650px",
  },
};

// Stats widget responsive positioning
export const statsWidgetConfig: ResponsiveValue<{
  bottom: string;
  left: string;
  width: string;
}> = {
  fourK: {
    bottom: "120px",
    left: "40px",
    width: "350px",
  },
  twoK: {
    bottom: "110px",
    left: "35px",
    width: "340px",
  },
  fhdToMacbook: {
    bottom: "100px",
    left: "30px",
    width: "300px", // Smaller for MacBook
  },
  large: {
    bottom: "95px",
    left: "25px",
    width: "280px",
  },
  medium: {
    bottom: "90px",
    left: "20px",
    width: "260px",
  },
  small: {
    bottom: "80px",
    left: "15px",
    width: "240px",
  },
  default: {
    bottom: "100px",
    left: "30px",
    width: "320px",
  },
};

// Header spacing configuration
export const headerSpacingConfig: ResponsiveValue<{
  logoLeft: string;
  rightSectionRight: string;
  buttonGap: string;
}> = {
  fourK: {
    logoLeft: "40px",
    rightSectionRight: "40px",
    buttonGap: "24px",
  },
  twoK: {
    logoLeft: "35px",
    rightSectionRight: "35px",
    buttonGap: "20px",
  },
  fhdToMacbook: {
    logoLeft: "28px",
    rightSectionRight: "28px",
    buttonGap: "16px",
  },
  large: {
    logoLeft: "24px",
    rightSectionRight: "24px",
    buttonGap: "14px",
  },
  medium: {
    logoLeft: "20px",
    rightSectionRight: "20px",
    buttonGap: "12px",
  },
  small: {
    logoLeft: "16px",
    rightSectionRight: "16px",
    buttonGap: "8px",
  },
  default: {
    logoLeft: "25px",
    rightSectionRight: "25px",
    buttonGap: "16px",
  },
};

// Floating Step Description Window configuration
// Position is now calculated dynamically based on SourceDoc config
export const floatingStepDescriptionConfig: ResponsiveValue<{
  width: number;
  gap: number;
}> = {
  fourK: {
    width: 800,
    gap: 30,
  },
  twoK: {
    width: 980,
    gap: 30,
  },
  fhdToMacbook: {
    width: 900, // Reasonable width for MacBook
    gap: 10,
  },
  large: {
    width: 750,
    gap: 10,
  },
  medium: {
    width: 500,
    gap: 15,
  },
  small: {
    width: 300,
    gap: 10,
  },
  default: {
    width: 600,
    gap: 25,
  },
};

// Header button configurations for simulation mode
export const headerButtonsConfig = {
  simulationMode: {
    size: {
      help: "16px",
      options: "22px",
      discord: "20px",
      file: "20px",
    },
    position: {
      help: "15px",
      options: "40px",
      discord: "65px",
      file: "90px",
    },
    containerHeight: "22px",
    topOffset: -5,
  },
  normalMode: {
    size: {
      help: "24px",
      options: "30px",
      discord: "25px",
      file: "24px",
    },
    containerHeight: "28px",
  },
};

// HideIrrelevantCellsToggle component responsive positioning
export const hideIrrelevantCellsToggleConfig: ResponsiveValue<{
  normalMode: string;
  runningSimulation: string;
}> = {
  fourK: {
    normalMode: "50px",
    runningSimulation: "70px",
  },
  twoK: {
    normalMode: "50px",
    runningSimulation: "70px",
  },
  fhdToMacbook: {
    normalMode: "70px", // +20px offset for smaller screens
    runningSimulation: "90px", // +20px offset for smaller screens
  },
  large: {
    normalMode: "70px",
    runningSimulation: "90px",
  },
  medium: {
    normalMode: "70px",
    runningSimulation: "90px",
  },
  small: {
    normalMode: "70px",
    runningSimulation: "90px",
  },
  default: {
    normalMode: "50px",
    runningSimulation: "70px",
  },
};

// InSimulationSnackBar component responsive positioning
export const inSimulationSnackBarConfig: ResponsiveValue<{
  normalMode: string;
  fullSizeMode: string;
}> = {
  fourK: {
    normalMode: "0px",
    fullSizeMode: "20px",
  },
  twoK: {
    normalMode: "0px",
    fullSizeMode: "20px",
  },
  fhdToMacbook: {
    normalMode: "20px", // +20px offset for smaller screens
    fullSizeMode: "40px", // +20px offset for smaller screens
  },
  large: {
    normalMode: "20px",
    fullSizeMode: "40px",
  },
  medium: {
    normalMode: "20px",
    fullSizeMode: "40px",
  },
  small: {
    normalMode: "20px",
    fullSizeMode: "40px",
  },
  default: {
    normalMode: "0px",
    fullSizeMode: "20px",
  },
};
